import { Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { filterSportsEmitLast } from '@frontend/sports/host-app/sports-product/feature/utils';
import { SyncUserSettingsService, UserService } from '@frontend/sports/user/feature';
import { Decimal } from 'decimal.js';
import { ArcRestrictionService } from 'packages/sports/web/app/src/arc/arc-restriction.service';

import { IBetslipBaseState } from '../../../base/store/state';
import { BetslipState } from '../../../core/betslip-state';
import { ResultStatus } from '../../../core/picks/pick-models';
import { isPlaceBlockingError, shouldThisPlacementErrorLockEditBet } from '../../../modules/validation/services/utils/betslip-errors-utils';
import { IQuickDepositState } from '../../quick-deposit/quick-deposit.state';
import { IBetslipTypeState } from '../../types/state';
import { BetslipError } from '../../validation/errors/betslip-error';
import { PickInvisible } from '../../validation/errors/result/pick-invisible';
import { NotEnoughMoney } from '../../validation/errors/user/not-enough-money';
import { IEditMyBetState } from '../state';
import { EditBetCheckingService } from './edit-bet-checking.service';

export enum EditBetButtonState {
    Locked,
    Disabled,
    Enabled,
    Processing,
    MakeDeposit,
    QuickDeposit,
    ProcessingDeposit,
}

@Injectable()
export class EditBetPlaceButtonStateService {
    isUserOnPlayBreak: boolean;

    constructor(
        private user: UserService,
        private betslipUserSettings: SyncUserSettingsService,
        private editMyBetCheckingService: EditBetCheckingService,
        private arcRestrictionService: ArcRestrictionService,
    ) {
        this.arcRestrictionService.isUserOnPlayBreak$.pipe(filterSportsEmitLast(), takeUntilDestroyed()).subscribe((data) => {
            this.isUserOnPlayBreak = data;
        });
    }

    buttonState(
        baseState: IBetslipBaseState,
        typesState: IBetslipTypeState,
        quickDepositState: IQuickDepositState,
        errors: BetslipError[],
    ): EditBetButtonState {
        if (quickDepositState.isProcessing && quickDepositState.isOpen) {
            return EditBetButtonState.ProcessingDeposit;
        }

        if (this.hasInsufficientFundsError(errors) && quickDepositState.isOpen) {
            return EditBetButtonState.QuickDeposit;
        }
        if (this.hasInsufficientFundsError(errors)) {
            return EditBetButtonState.MakeDeposit;
        }
        if (baseState.state === BetslipState.Placing) {
            return EditBetButtonState.Processing;
        }
        if (this.isEarlyPayoutDisabled(typesState.editBet) || this.isEditBetLockedByPlacementError(errors)) {
            return EditBetButtonState.Locked;
        }
        if (this.isUserOnPlayBreak || !this.canPlaceBet(typesState, errors)) {
            return EditBetButtonState.Disabled;
        }

        return EditBetButtonState.Enabled;
    }

    private isEarlyPayoutDisabled(editBet: IEditMyBetState): boolean {
        const earlyPayoutStake = editBet.earlyPayoutStake;

        return earlyPayoutStake === null || earlyPayoutStake === 0;
    }

    private isEditBetLockedByPlacementError(errors: BetslipError[]): boolean {
        return errors.some((error) => shouldThisPlacementErrorLockEditBet(error));
    }

    private hasInsufficientFundsError(errors: BetslipError[]): boolean {
        return errors.some((error) => error instanceof NotEnoughMoney);
    }

    canPlaceBet(types: IBetslipTypeState, errors: BetslipError[]): boolean {
        return (
            this.user.isAuthenticated &&
            !errors.some((e) => isPlaceBlockingError(e)) &&
            !errors.some((e) => e instanceof PickInvisible) &&
            this.isStakeValid(types.editBet) &&
            this.hasChanges(types) &&
            !types.editBet.isEarlyPayoutStakeLoading &&
            !this.arePicksClosed(types.editBet)
        );
    }

    private arePicksClosed(editBet: IEditMyBetState): boolean {
        const picksNotAvailable = editBet.picks.pickList.filter((p) => p.resultStatus !== ResultStatus.Open && p.resultStatus !== ResultStatus.Won);

        const picksNotRemoved = picksNotAvailable.filter(
            (p) => editBet.picks.pickState[p.id.toString()] && !editBet.picks.pickState[p.id.toString()].isRemoved,
        );

        return !!picksNotRemoved.length;
    }

    private isStakeValid(editMyBetState: IEditMyBetState): boolean {
        if (editMyBetState.earlyPayoutStake === null) {
            return false;
        }

        const totalStake = new Decimal(editMyBetState.earlyPayoutStake).add(editMyBetState.addedStake);

        return totalStake.greaterThanOrEqualTo(this.betslipUserSettings.configSettings.minimumStake);
    }

    private hasChanges(types: IBetslipTypeState): boolean {
        return this.editMyBetCheckingService.hasChanges(types);
    }
}
